import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MdArrowBack, MdKeyboardArrowDown, MdKeyboardArrowRight } from 'react-icons/md';
import API from '../../services/api';
import AccountDetail, { Illustrative } from './AccountDetail';
import { fmt$, signed$, fmtPctNeg, fmtDate, fmtDateShort, pnlClr } from './fmt';
import { ILLUSTRATIVE_LIST, adaptTradingList } from './tradingAdapter';
import LoadingSpinner from '../../components/LoadingSpinner';

// The Automated Trading module — restructured from a single-account page into a PORTFOLIO
// LIST → DETAIL. This file holds the view STATE and the two surfaces; the per-account render
// body lives in AccountDetail. It is future-proofed for many accounts but works today with the
// one the live feed sends.
//
// TWO SOURCES, ONE LIST. On mount the page asks the research feed for a `trading` block
// (/api/research-feed/trading/). adaptTradingList normalizes whatever container shape the feed
// uses — a single account today, an array or an `accounts` wrapper later — into a LIST of
// account view-models. If any real account comes back the list renders it with the banner hidden
// and a dated "As at" line; if the feed is empty or errors, it falls back to a ONE-account
// illustrative list AND shows the banner — byte-identical, per account, to before the feed
// existed. The adapter is the ONLY place the shapes are reconciled; no surface below branches on
// the source.
//
// WITH ONE ACCOUNT THE LIST STILL SHOWS ONE ROW. That is intended: the surface must not
// auto-skip to detail, because the day a second account arrives the reader must already know the
// list is where accounts live — a page that jumps straight to detail with one account would
// silently change shape the moment a second one appears.

// A %return fraction on the VM → percent for display. NO leading "+" on positives — direction
// is carried by the green/red text colour, so a "+" would be redundant; a minus shows only when
// negative. (PROFIT is the exception and keeps its sign via signed$.)
const fmtRet = (x) => fmtPctNeg(x == null ? null : x * 100, 2);

// ── PERIOD PILLS ──────────────────────────────────────────────────────────────────────
// The list can be read over YTD / QTD / MTD / WTD or the whole history ("All"). A pill
// retunes ALL FOUR figure columns — PROFIT, RETURN, SPX and ALPHA. RETURN/SPX/ALPHA are
// derived client-side from the cumulative-return curves (see accountPeriod); PROFIT now
// follows the pill too, reading the source-sent `profit_by_period` (see periodProfit). The
// source's period boundaries match ours EXACTLY — Monday-start ISO week, calendar YTD/QTD/MTD,
// all UTC — so the period Profit lines up with the period Return. "All" (the since-inception
// total) always has data, so it is the default. Where a period value is genuinely absent
// (the illustrative fallback, which carries no per-period series) PROFIT shows "—", never the
// since-inception number under a period heading and never a fabricated split.
const PERIODS = ['YTD', 'QTD', 'MTD', 'WTD', 'All'];

// Pill → the profit_by_period key. "All" has no key → the since-inception total.
const PROFIT_KEY = { YTD: 'ytd', QTD: 'qtd', MTD: 'mtd', WTD: 'wtd' };

// The PROFIT figure for an account or a basket over the selected period. "All" → the
// since-inception total the wire sends (an account's totalPnl / a basket's profit). A period →
// the matching profit_by_period value IF the source sent one; if it did not, the figure is
// UNKNOWN for that window → null (rendered "—"), never the since-inception number relabelled as
// the period and never an invented split. The source sends profit_by_period for every real
// account and basket, so a period always resolves in practice; the null path guards only the
// illustrative fallback (no per-period series).
function periodProfit(sinceInception, byPeriod, period) {
  if (period === 'All') return sinceInception;
  const k = PROFIT_KEY[period];
  if (k && byPeriod && byPeriod[k] != null) return byPeriod[k];
  return null;
}

// The reference "now" for period boundaries is the DATA's as-of date (the account VM's
// `dataAsOf` — the trading block's own `as_at`, the close the curve and profit_by_period are
// computed against), NOT the wall clock and NOT the push's send time. All three can differ: a
// Monday-morning push (send time = Monday) carries Friday's book (data date = Friday). Anchoring
// WTD to Monday points at a not-yet-started week — Return "—", one curve point — while the
// source's profit_by_period, computed as-of Friday, still reports the prior week, so the two
// columns describe DIFFERENT weeks. Anchoring to the data date makes them the same week. Falls
// back to Date.now() only if the date is missing/unparseable (never for real feed data).
function refNowMs(dataAsOf) {
  const t = dataAsOf ? new Date(dataAsOf).getTime() : NaN;
  return Number.isFinite(t) ? t : Date.now();
}

// The UTC start-of-period boundary (ms) for a pill, or null for "All" (the whole curve). The
// reference instant is `refMs` (the data as-of date — see refNowMs), never argless `new Date()`,
// and every boundary is built in UTC so it lines up with the curve's ISO date strings (UTC dates).
function periodStartMs(period, refMs) {
  if (period === 'All') return null;
  const now = new Date(refMs);
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  const d = now.getUTCDate();
  if (period === 'YTD') return Date.UTC(y, 0, 1);
  if (period === 'QTD') return Date.UTC(y, Math.floor(m / 3) * 3, 1);
  if (period === 'MTD') return Date.UTC(y, m, 1);
  if (period === 'WTD') {
    // Monday of the current ISO week: getUTCDay() is 0=Sun..6=Sat, so Monday is (dow+6)%7 days back.
    const dow = new Date(Date.UTC(y, m, d)).getUTCDay();
    return Date.UTC(y, m, d) - ((dow + 6) % 7) * 86400000;
  }
  return null;
}

// The return over [periodStart, latest] from ONE rebased cumulative-return curve. Both the
// equity and benchmark curves are cumulative-return decimals rebased to 0 at inception, so a
// later window's return = (1 + cum_at_latest) / (1 + cum_at_period_start) − 1. The period
// start point is the FIRST curve point on/after the boundary. If the window holds fewer than
// two points (e.g. WTD on a weekly curve), the return is UNKNOWN → null, never fabricated.
function curvePeriodReturn(curve, startMs) {
  const pts = (Array.isArray(curve) ? curve : []).filter((p) => p && p.date && p.value != null);
  if (pts.length < 2) return null;
  let i0 = 0;
  if (startMs != null) {
    i0 = pts.findIndex((p) => new Date(p.date).getTime() >= startMs);
    if (i0 < 0) return null;               // no curve point in the window
  }
  if (pts.length - i0 < 2) return null;    // fewer than 2 points in range → don't invent one
  const vStart = pts[i0].value;
  const vEnd = pts[pts.length - 1].value;
  return (1 + vEnd) / (1 + vStart) - 1;
}

// {ret, bench, out} for an account over the selected period. "All" uses the account's stored
// since-inception figures (byte-identical to what the row showed before pills existed); every
// other period is derived client-side from the rebased curves. `out` = return − benchmark
// (outperformance in points), null unless both legs are known.
function accountPeriod(a, period) {
  const ref = refNowMs(a.dataAsOf);   // anchor windows to the DATA's as-of date, not the send time
  const ret = period === 'All' ? a.pctReturn : curvePeriodReturn(a.equityCurve, periodStartMs(period, ref));
  const bench = period === 'All' ? a.benchReturn : curvePeriodReturn(a.benchmarkCurve, periodStartMs(period, ref));
  const out = ret != null && bench != null ? ret - bench : null;
  return { ret, bench, out };
}

// The five aligned columns — NAME | PROFIT | RETURN | SPX | ALPHA — as ONE grid-track
// definition shared by the header strip, every parent row AND the indented bucket children, so
// a column and its heading can never drift apart. NAME takes the left and shrinks/truncates so
// the numbers get room; the four numeric columns share EQUAL tracks and are right-aligned, so
// they line up as real columns top-to-bottom. Generous inter-column gaps (bumped up now that the
// arrows, the three "+" signs and the old right-hand chevron column are gone) give each figure
// clear tabular separation. This is the SAME single aligned row at every width — never stacked.
const COLS = 'grid grid-cols-[minmax(0,1.3fr)_repeat(4,minmax(0,1fr))] gap-x-3 sm:gap-x-6';

// The BASKET child grid (NEW payload only) — its own column set because basket_performance rows
// carry DIFFERENT figures from the parent's Profit/Return/SPX/Alpha: NAME | PNL | # | WEIGHT |
// SINCE. Shared by the basket sub-header and every basket row so a heading can never sit over the
// wrong figure. NAME leads and WRAPS (never truncates the strategy class); the four right-aligned
// tracks stay narrow so all five fit at 375px. PNL is the first figure, echoing the parent row.
const BCOLS = 'grid grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,0.5fr)_minmax(0,0.8fr)_minmax(0,1.05fr)] gap-x-1.5 sm:gap-x-4';

// One right-aligned numeric cell. Direction is carried by COLOUR alone (green +, red −); there
// are no arrow glyphs anywhere. Every cell is a SINGLE value on one line, so — with the row's
// items-center — the name and all four numbers sit on one shared baseline, nothing staggered.
const Num = ({ value, cls }) => (
  <div className={`whitespace-nowrap text-right font-mono text-[11px] leading-tight tabular-nums sm:text-[13px] ${cls}`}>
    {value}
  </div>
);

// The column-header strip above the rows, shown at EVERY width. It mirrors each row's flex
// geometry — `px-4`, then a `w-6` spacer standing in for the row's left disclosure chevron, then
// a `flex-1` grid over the shared COLS tracks — so each heading sits exactly over its column.
// The PROFIT note tracks the active pill ("since inception" on All, else the period) so the
// column's window is never mislabelled; it shows on wider screens only.
const ListHeader = ({ period }) => {
  const th = 'text-right text-[8px] font-medium uppercase tracking-[0.1em] text-gray-400 sm:text-[9px] sm:tracking-[0.13em]';
  return (
    <div className="mb-1.5 flex items-end px-4">
      <div className="w-6 shrink-0" aria-hidden="true" />
      <div className={`${COLS} flex-1 items-end`}>
        <div className="text-[8px] font-medium uppercase tracking-[0.1em] text-gray-400 sm:text-[9px] sm:tracking-[0.13em]">Name</div>
        <div className={th}>
          Profit
          <span className="hidden text-[8px] font-normal normal-case tracking-normal text-gray-400 sm:block">
            {period === 'All' ? 'since inception' : period}
          </span>
        </div>
        <div className={th}>Return</div>
        <div className={th}>SPX</div>
        <div className={th}>Alpha</div>
      </div>
    </div>
  );
};

// One PARENT row in the tree-table: a small left disclosure chevron that expands the account's
// strategy-bucket CHILD rows, then the row body (name + the four figures) which opens detail.
// The chevron and the body are SIBLING buttons, not nested, so the two tap targets never
// overlap — left chevron = expand/collapse children; row body = open AccountDetail.
//
// Every row (this parent, the header strip, and each child) shares the same `px-4` + `w-6`
// chevron slot + `flex-1` COLS grid, so the tree indents cleanly and columns line up top to
// bottom no matter how many accounts there are later.
function AccountRow({ account, onOpen, period }) {
  const [open, setOpen] = useState(false);
  const a = account;
  // The raw source label (e.g. Live_Mahroof_1) is DELIBERATELY never shown — the owner's
  // decision. A reader sees only the display name ("Genesis"); the source label stays internal.
  const buckets = a.byBucket || [];
  // The NEW feed sends basket_performance — the source's "expandable table under the account".
  // When it is present AND non-empty, the chevron reveals BASKET rows instead of the buckets[]
  // allocation children. When absent (today's live payload), the buckets children render exactly
  // as before. An empty basket array falls back to buckets rather than showing nothing.
  const baskets = Array.isArray(a.basketPerformance) ? a.basketPerformance : null;
  const useBaskets = !!(baskets && baskets.length);

  // All four figures follow the selected pill: RETURN/SPX/ALPHA from the curves (accountPeriod),
  // PROFIT from the source-sent profit_by_period (periodProfit). "All" → since-inception; a period
  // with no value → "—".
  const { ret, bench, out } = accountPeriod(a, period);
  const profit = periodProfit(a.totalPnl, a.profitByPeriod, period);

  return (
    <div className="rounded-xl border border-gray-200 bg-white transition-colors hover:border-brand-gold dark:border-gray-800 dark:bg-[#161b22]">
      {/* PARENT ROW */}
      <div className="flex items-center px-4 py-3.5">
        {/* Left disclosure — small tree-style chevron, right before the name. Toggles children. */}
        <button
          type="button" onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={`${open ? 'Hide' : 'Show'} ${useBaskets ? 'strategy baskets' : 'strategy buckets'}`}
          className="grid w-6 shrink-0 place-items-center text-gray-400 transition-colors hover:text-brand-gold"
        >
          {open ? <MdKeyboardArrowDown size={18} /> : <MdKeyboardArrowRight size={18} />}
        </button>
        {/* Row body — tapping it opens detail. NAME | PROFIT | RETURN | SPX | ALPHA over the
            shared COLS tracks, vertically centred so the name and all four numbers sit on one
            baseline. No arrows, no sublines; direction is colour only. The name truncates when
            space is tight so the four numbers always fit on the one line. */}
        <button
          type="button" onClick={() => onOpen(a)}
          className="min-w-0 flex-1 text-left"
        >
          <div className={`${COLS} items-center`}>
            <div className="min-w-0 truncate font-serif text-[13px] leading-tight text-gray-900 sm:text-[17px] dark:text-gray-100">
              {a.displayName}
            </div>
            {/* PROFIT — the period's P&L (source profit_by_period), or since-inception on "All".
                Follows the pill; keeps its sign; "—" when a period value is absent. */}
            <Num value={signed$(profit)} cls={pnlClr(profit)} />
            {/* RETURN — the period return; SPX — the benchmark's period return. */}
            <Num value={fmtRet(ret)} cls={pnlClr(ret)} />
            <Num value={fmtRet(bench)} cls={pnlClr(bench)} />
            {/* ALPHA — return − benchmark (`out`); "—" when either leg is unknown. */}
            <Num value={fmtRet(out)} cls={pnlClr(out)} />
          </div>
        </button>
      </div>

      {/* CHILD ROWS — indented under the NAME column in the SAME tree-table.
          NEW payload: basket_performance rows (per-strategy-class performance). OLD payload:
          the buckets[] allocation rows exactly as before. The parent chevron reveals whichever
          the account carries; the two never mix. */}
      {open && (
        <div className="border-t border-gray-100 pb-2 pt-1 dark:border-gray-800">
          {useBaskets ? (
            <>
              {/* Basket SUB-HEADER — labels the basket columns, which differ from the parent's
                  Profit/Return/SPX/Alpha, so "478" is never read under a "Return" heading. Mirrors
                  each basket row's geometry (px-4 + w-6 indent slot + flex-1 BCOLS grid). */}
              <div className="flex items-end px-4 pb-1 pt-0.5">
                <div className="w-6 shrink-0" aria-hidden="true" />
                <div className={`${BCOLS} flex-1 items-end`}>
                  <div className="text-[8px] font-medium uppercase tracking-[0.1em] text-gray-400">Name</div>
                  <div className="text-right text-[8px] font-medium uppercase tracking-[0.1em] text-gray-400">Profit</div>
                  <div className="text-right text-[8px] font-medium uppercase tracking-[0.1em] text-gray-400">#</div>
                  <div className="text-right text-[8px] font-medium uppercase tracking-[0.1em] text-gray-400">Weight</div>
                  <div className="text-right text-[8px] font-medium uppercase tracking-[0.1em] text-gray-400">Since</div>
                </div>
              </div>
              {baskets.map((b) => {
                // A basket carries its OWN performance — these figures are STANDALONE and do NOT
                // sum to the account. Only WEIGHT (aum_pct_of_gross) sums to ~100%. Fraction ×100.
                const weight = b.pctOfGross == null ? null : b.pctOfGross * 100;
                return (
                  <div key={b.label} className="flex items-center px-4 py-1.5">
                    <div className="w-6 shrink-0" aria-hidden="true" />
                    <div className={`${BCOLS} flex-1 items-center`}>
                      {/* NAME — a single clean line: the strategy class. The raw underscore basket
                          `label` ("relative_momentum — 2024_dec_6_aug") is deliberately NOT shown.
                          Normal case: strategy_class is a real class ("Relative Momentum") → use it.
                          Safety fallback only: if strategy_class is empty or the literal
                          "Unclassified" placeholder, fall back to `label` as the primary (the only
                          real name available then), rendered verbatim — never title-cased,
                          date-stripped or parsed. Either way there is NO secondary line. Primary
                          WRAPS, never truncated. */}
                      <div className="min-w-0">
                        <div className="break-words text-[11.5px] font-medium leading-tight text-gray-800 dark:text-gray-200">
                          {b.strategyClass && b.strategyClass !== 'Unclassified' ? b.strategyClass : b.label}
                        </div>
                      </div>
                      {/* Profit — the basket's P&L ($), signed, green/red. First figure column.
                          Follows the pill like the parent Profit column: the period's
                          profit_by_period, since-inception on "All", "—" when a period is absent. */}
                      {(() => {
                        const bp = periodProfit(b.profit, b.profitByPeriod, period);
                        return (
                          <div className={`whitespace-nowrap text-right font-mono text-[10px] tabular-nums sm:text-[12px] ${pnlClr(bp)}`}>
                            {signed$(bp)}
                          </div>
                        );
                      })()}
                      {/* # — strategy count, plain integer. */}
                      <div className="whitespace-nowrap text-right font-mono text-[10px] tabular-nums text-gray-600 sm:text-[12px] dark:text-gray-300">
                        {b.nStrategies == null ? '—' : b.nStrategies}
                      </div>
                      {/* WEIGHT — % of gross deployed. */}
                      <div className="whitespace-nowrap text-right font-mono text-[10px] tabular-nums text-gray-600 sm:text-[12px] dark:text-gray-300">
                        {weight == null ? '—' : `${weight.toFixed(1)}%`}
                      </div>
                      {/* SINCE — basket start date, short format. */}
                      <div className="whitespace-nowrap text-right font-mono text-[9px] tabular-nums text-gray-500 sm:text-[11px]">
                        {b.startDate ? fmtDateShort(b.startDate) : '—'}
                      </div>
                    </div>
                  </div>
                );
              })}
              {/* Footnote: what this account IS (source-sent description). Until the source ships
                  a description, fall back to the honest basket caveat so the space is never empty. */}
              <div className="flex px-4">
                <div className="w-6 shrink-0" aria-hidden="true" />
                <p className="flex-1 pl-4 pt-1 text-[10px] leading-relaxed text-gray-400">
                  {a.description
                    || 'Basket figures are standalone — they do not sum to the account; only Weight (% of gross) sums to 100%.'}
                </p>
              </div>
            </>
          ) : buckets.length === 0 ? (
            <div className="flex items-center px-4 py-1.5">
              <div className="w-6 shrink-0" aria-hidden="true" />
              <p className="flex-1 pl-4 text-xs italic text-gray-400">No strategy buckets reported.</p>
            </div>
          ) : (
            <>
              {buckets.map((b) => {
                // % of the account's INVESTED capital (AUM − cash), per the contract — NOT the
                // detail's share-of-deployed. A negative bucket (Reserve) yields a negative %/$,
                // shown honestly in red.
                const pct = a.invested ? (b.marketValue / a.invested) * 100 : null;
                const neg = b.marketValue < 0;
                const cls = neg ? 'text-red-700 dark:text-red-400' : 'text-gray-500 dark:text-gray-400';
                return (
                  <div key={b.label} className="flex items-center px-4 py-1">
                    <div className="w-6 shrink-0" aria-hidden="true" />
                    {/* Children DON'T use the parent COLS grid: their numeric columns are blank, so
                        the bucket name would be needlessly boxed into the narrow NAME track. Instead
                        the name takes the full row width (minus the allocation) via flex, so full
                        labels like "Trend Following" show without truncation; allocation stays
                        right-aligned at the far edge. Indent (empty w-6 slot + pl-4) is preserved. */}
                    <div className="flex flex-1 items-center gap-3">
                      <div className="min-w-0 flex-1 truncate pl-4 text-xs text-gray-700 dark:text-gray-300" title={b.label}>
                        {b.label}
                      </div>
                      <div className={`shrink-0 whitespace-nowrap text-right font-mono text-[11px] tabular-nums sm:text-[13px] ${cls}`}>
                        {fmt$(b.marketValue)}
                        <span className="ml-2 text-gray-400">{pct == null ? '—' : `${pct.toFixed(1)}%`}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
              {/* Honest note: the feed carries no per-bucket P&L, so children show allocation only. */}
              <div className="flex px-4">
                <div className="w-6 shrink-0" aria-hidden="true" />
                <p className="flex-1 pl-4 pt-1 text-[10px] leading-relaxed text-gray-400">
                  Share of invested capital (AUM − cash). The feed carries no per-bucket P&amp;L, so buckets show allocation only.
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function AutomatedTrading() {
  // NULL = still loading. It must NOT start on the illustrative list: the feed is live now, so
  // seeding illustrative would paint wrong numbers and the "not real" banner for the moment the
  // fetch is in flight, then flip to real — the exact flash the owner saw. Load through a spinner
  // instead, and only ever show illustrative once we KNOW no real block is coming (empty feed or
  // error). A spinner is honest about not-knowing-yet; a flash of wrong figures is not.
  const [accounts, setAccounts] = useState(null);
  // Which account's detail is open. NULL = the list view. The list is always the default; with
  // one account it still shows one row (see the header note) — we never auto-skip to detail.
  const [selected, setSelected] = useState(null);
  // The RETURN/SPX/ALPHA window. "All" is the default because it always has data.
  const [period, setPeriod] = useState('All');

  useEffect(() => {
    let alive = true;
    API.get('/api/research-feed/trading/')
      .then((r) => {
        if (!alive) return;
        const list = adaptTradingList(r.data && r.data.trading, r.data && r.data.as_at);
        setAccounts(list || ILLUSTRATIVE_LIST);   // real accounts → live figures; else illustrative
      })
      .catch(() => { if (alive) setAccounts(ILLUSTRATIVE_LIST); });   // feed error → illustrative fallback
    return () => { alive = false; };
  }, []);

  // Shared page chrome: the way back to the rest of the portal.
  const Crumb = () => (
    <div className="mb-4 flex items-center gap-2 text-[11px]">
      <Link to="/" className="flex shrink-0 items-center gap-1 text-gray-400 transition-colors hover:text-brand-gold dark:text-gray-500">
        <MdArrowBack size={14} />
        All areas
      </Link>
    </div>
  );

  // While the feed is resolving, show the way back and a spinner — never illustrative figures or
  // the banner, which would be a claim we cannot yet make either way.
  if (!accounts) {
    return (
      <div>
        <Crumb />
        <h1 className="mb-4 font-serif text-2xl text-gray-900 dark:text-gray-100">Automated Trading</h1>
        <LoadingSpinner message="Loading…" />
      </div>
    );
  }

  const isReal = !!(accounts[0] && accounts[0].isReal);
  const asAt = accounts[0] && accounts[0].asAt;

  // ── DETAIL ── one account, reached by tapping its row; a clear way back to the list.
  if (selected) {
    return (
      <div>
        <div className="mb-4 flex items-center gap-2 text-[11px]">
          <button
            type="button" onClick={() => setSelected(null)}
            className="flex shrink-0 items-center gap-1 text-gray-400 transition-colors hover:text-brand-gold dark:text-gray-500"
          >
            <MdArrowBack size={14} />
            Back to portfolios
          </button>
        </div>
        <AccountDetail account={selected} />
      </div>
    );
  }

  // ── LIST ── the default view: one row per account.
  return (
    <div>
      <Crumb />

      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h1 className="font-serif text-2xl text-gray-900 dark:text-gray-100">Automated Trading</h1>
        {isReal && asAt && <span className="text-[11px] text-gray-400">As at {fmtDate(asAt)}</span>}
      </div>

      {/* The banner renders ONLY on the illustrative fallback. When live figures are showing it
          is hidden — the "As at" dateline above is what qualifies them instead. */}
      {!isReal && <Illustrative />}

      {/* Period pills — styled like the research chips. They retune RETURN / SPX / ALPHA only. */}
      <div className="mb-4">
        <div className="mb-2 text-[10px] font-medium uppercase tracking-[0.15em] text-gray-400">Period</div>
        <div className="scrollbar-thin -mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
          {PERIODS.map((p) => {
            const on = period === p;
            return (
              <button
                key={p} type="button" onClick={() => setPeriod(p)} aria-pressed={on}
                className={`flex shrink-0 items-center rounded-full border px-3 py-1.5 text-[13px] transition-colors ${on
                  ? 'border-brand-gold bg-brand-gold/10 text-brand-gold'
                  : 'border-gray-200 text-gray-600 hover:border-brand-gold dark:border-gray-800 dark:text-gray-300'}`}
              >
                {p}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mb-2 text-[10px] font-medium uppercase tracking-[0.15em] text-gray-400">
        {accounts.length === 1 ? '1 portfolio' : `${accounts.length} portfolios`}
      </div>
      <ListHeader period={period} />
      <div className="flex flex-col gap-2.5">
        {accounts.map((a) => (
          <AccountRow key={a.accountLabel || a.displayName} account={a} onOpen={setSelected} period={period} />
        ))}
      </div>
    </div>
  );
}
